import { useCallback, useEffect, useRef, useState } from "react";
import {
  FIELD_HEIGHT,
  FIELD_WIDTH,
  LEADERS,
  TIERS,
  createBattle,
  drawBattle,
  pickPriorityTarget,
  setLeader,
  snapshot,
  stepBattle,
  toggleSpeed,
  useOrder,
} from "./battleEngine.js";

const PHASES = {
  1: { kicker: "第一局面", name: "戦線維持", hint: "味方と合流し、敵の流れを止める" },
  2: { kicker: "第二局面", name: "敵軍増援", hint: "大群を焼くか、崩れた戦線を支える" },
  3: { kicker: "最終局面", name: "敵将斬首", hint: "道を開き、魔族隊長へ突入する" },
};

const ORDER_NAMES = {
  vanguard: "三傑大突撃",
  mage: "天墜星",
  guardian: "不倒陣",
};

function formatTime(seconds) {
  const value = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
}

function LeaderButton({ id, active, onClick }) {
  const leader = LEADERS[id];
  return (
    <button
      type="button"
      className={`leader-card leader-${id} ${active ? "is-active" : ""}`}
      onClick={() => onClick(id)}
      aria-pressed={active}
      aria-label={`${leader.name}を先頭にする。${leader.role}陣形`}
    >
      <span className="portrait" aria-hidden="true">{leader.icon}</span>
      <span className="leader-copy">
        <b>{leader.name}</b>
        <small>{leader.role}</small>
      </span>
      <span className="formation-mark">{active ? "先頭" : "交代"}</span>
    </button>
  );
}

export function Game() {
  const canvasRef = useRef(null);
  const battleRef = useRef(null);
  const frameRef = useRef(0);
  const lastRef = useRef(0);
  const [tier, setTier] = useState("elite");
  const [runId, setRunId] = useState(0);
  const [guideOpen, setGuideOpen] = useState(true);
  const [ui, setUi] = useState(() => snapshot(createBattle(tier)));

  useEffect(() => {
    const battle = createBattle(tier);
    battleRef.current = battle;
    window.__THREE_HEROES_BATTLE__ = battle;
    setUi(snapshot(battle));
    lastRef.current = performance.now();
    let lastUiUpdate = 0;

    const loop = (now) => {
      const dt = Math.min((now - lastRef.current) / 1000, 0.1);
      lastRef.current = now;
      stepBattle(battle, dt);
      const canvas = canvasRef.current;
      if (canvas) {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        if (canvas.width !== FIELD_WIDTH * dpr || canvas.height !== FIELD_HEIGHT * dpr) {
          canvas.width = FIELD_WIDTH * dpr;
          canvas.height = FIELD_HEIGHT * dpr;
        }
        const context = canvas.getContext("2d");
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
        drawBattle(context, battle);
      }
      if (now - lastUiUpdate > 100) {
        setUi(snapshot(battle));
        lastUiUpdate = now;
      }
      frameRef.current = requestAnimationFrame(loop);
    };
    frameRef.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(frameRef.current);
      if (window.__THREE_HEROES_BATTLE__ === battle) delete window.__THREE_HEROES_BATTLE__;
    };
  }, [tier, runId]);

  const changeLeader = useCallback((leader) => {
    if (!battleRef.current) return;
    setLeader(battleRef.current, leader);
    setUi(snapshot(battleRef.current));
  }, []);

  const fireOrder = useCallback(() => {
    if (!battleRef.current) return;
    useOrder(battleRef.current);
    setUi(snapshot(battleRef.current));
  }, []);

  const handleFieldTap = useCallback((event) => {
    const canvas = canvasRef.current;
    const battle = battleRef.current;
    if (!canvas || !battle) return;
    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) * FIELD_WIDTH / rect.width;
    const y = (event.clientY - rect.top) * FIELD_HEIGHT / rect.height;
    pickPriorityTarget(battle, x, y);
    setUi(snapshot(battle));
  }, []);

  const phase = PHASES[ui.phase];
  const leader = LEADERS[ui.leader];
  const bossVisible = ui.bossHp !== null;
  const statusTitle = ui.status === "victory" ? "戦線勝利" : "戦線崩壊";

  return (
    <main className={`game-shell tier-${tier}`}>
      <section className="game-header" aria-label="戦況">
        <div className="mission-title">
          <span>{phase.kicker}</span>
          <strong>{phase.name}</strong>
        </div>
        <div className="phase-hint">{phase.hint}</div>
        <div className="battle-counters">
          <div><small>TIME</small><b>{formatTime(ui.time)}</b></div>
          <div><small>味方</small><b>{ui.allies}</b></div>
          <div><small>敵勢</small><b>{ui.enemies}</b></div>
        </div>
        <button
          type="button"
          className="icon-button"
          onClick={() => setGuideOpen((open) => !open)}
          aria-label="遊び方"
        >？</button>
      </section>

      <section className="battle-wrap" aria-label="自動戦闘フィールド">
        <canvas
          ref={canvasRef}
          className="battle-canvas"
          width={FIELD_WIDTH}
          height={FIELD_HEIGHT}
          onPointerDown={handleFieldTap}
        />

        <div className="tier-switch" aria-label="成長段階デバッグ">
          <span>成長比較</span>
          {Object.entries(TIERS).map(([id, item]) => (
            <button
              type="button"
              key={id}
              className={tier === id ? "is-active" : ""}
              onClick={() => setTier(id)}
            >{item.name}</button>
          ))}
        </div>

        <button
          type="button"
          className="speed-button"
          onClick={() => {
            toggleSpeed(battleRef.current);
            setUi(snapshot(battleRef.current));
          }}
        >検証速度 ×{ui.speed}</button>

        {bossVisible && (
          <div className="boss-meter">
            <span>優先目標</span>
            <b>魔族隊長</b>
            <i><span style={{ width: `${Math.max(0, ui.bossHp * 100)}%` }} /></i>
          </div>
        )}

        {ui.messageLife > 0 && <div className="battle-message">{ui.message}</div>}

        {guideOpen && (
          <aside className="quick-guide">
            <button type="button" onClick={() => setGuideOpen(false)} aria-label="閉じる">×</button>
            <strong>三人の誰を前に出す？</strong>
            <p><em className="vanguard-dot" />剣は強敵へ突破　<em className="mage-dot" />星は大群を殲滅　<em className="guardian-dot" />盾は味方を救援</p>
            <small>大型敵や敵将を直接タップすると優先目標にできます。移動と通常攻撃は自動です。</small>
          </aside>
        )}

        {(ui.status === "victory" || ui.status === "defeat") && (
          <div className={`result-overlay result-${ui.status}`}>
            <span>{ui.status === "victory" ? "MISSION COMPLETE" : "MISSION FAILED"}</span>
            <h1>{statusTitle}</h1>
            <p>{ui.status === "victory" ? "三人が開いた道を、人類軍が一斉に進む。" : "役割を切り替え、味方軍と戦線を取り戻そう。"}</p>
            <div className="result-stats">
              <div><small>撃破</small><b>{ui.stats.kills}</b></div>
              <div><small>先頭交代</small><b>{ui.stats.switches}</b></div>
              <div><small>号令</small><b>{ui.stats.orders}</b></div>
            </div>
            <button type="button" onClick={() => setRunId((id) => id + 1)}>同じ段階でもう一戦</button>
          </div>
        )}
      </section>

      <section className="command-deck" aria-label="三傑の先頭交代と号令">
        <div className="party-vitals">
          <div className="party-name"><span>TRIO</span><b>暁の三傑</b></div>
          <div className="hp-bar" aria-label={`三人の体力 ${Math.round(ui.hp)} / ${ui.maxHp}`}>
            <span style={{ width: `${Math.max(0, ui.hp / ui.maxHp * 100)}%` }} />
          </div>
        </div>

        <div className="leader-grid">
          {Object.keys(LEADERS).map((id) => (
            <LeaderButton key={id} id={id} active={ui.leader === id} onClick={changeLeader} />
          ))}
        </div>

        <button
          type="button"
          className={`order-button order-${ui.leader} ${ui.gauge >= 100 ? "is-ready" : ""}`}
          disabled={ui.gauge < 100 || ui.status !== "playing"}
          onClick={fireOrder}
          style={{ "--gauge": `${ui.gauge}%`, "--leader-color": leader.color }}
        >
          <span>号令</span>
          <b>{ORDER_NAMES[ui.leader]}</b>
          <i>{ui.gauge >= 100 ? "発動" : `${Math.floor(ui.gauge)}%`}</i>
        </button>
      </section>

      <div className="portrait-warning">
        <strong>端末を横向きにしてください</strong>
        <span>このプロトタイプはスマホ横画面向けです</span>
      </div>
    </main>
  );
}
